import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import { PageWrapper } from 'components-antd';
import { Wrapper } from 'components';
import { CalendarTiles, Header, NavButtonLeft, NavButtonRight, DayView } from './components';
import Task from 'pages/Workshop/Transactions/TransactionTasks/components/Task';

import { CalendarView, defaultCalendarTypeOptions } from './constants';
import { formatDate, isToday } from 'helpers';
import {
  getCalendarFiltersAppliedSelector,
  getWorkshopCalendarStatsSelector,
} from 'store/selectors/calendar';
import {
  getWorkshopCalendarStatsEffect,
  requestGetProjectsEffect,
  requestGetTransactionPersons,
  requestGetTransactionsEffect,
  setCalendarFiltersEffect,
} from 'store/effects';
import { getAgentTeamStatsSelector } from 'store/selectors/clarity';
import { getAgentTeamRoleSelector } from 'store/selectors/agentTeamDetail';

import { TeamFilterType } from 'app-constants/enums/teamFilter';
import { TEAM_BASIC } from 'settings/constants/roles';

import { LocalStorage, LocationService } from 'services';

import styles from './styles.module.scss';

export const Calendar = () => {
  const locationService = new LocationService();
  const query = locationService.setLocation(location).getQuery();
  const dispatch = useDispatch();

  const [currentDate, setCurrentDate] = useState(moment());
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState(null);
  const { data, isPending } = useSelector(getWorkshopCalendarStatsSelector);
  const filters = useSelector(getCalendarFiltersAppliedSelector);

  const [view, setView] = useState<CalendarView.MONTH | CalendarView.WEEK | CalendarView.DAY>(
    query?.dueToday || query?.overdueMilestones
      ? CalendarView.DAY
      : query?.dueWeek
      ? CalendarView.WEEK
      : (LocalStorage.getCalendarViewType() as
          | CalendarView.MONTH
          | CalendarView.WEEK
          | CalendarView.DAY) || CalendarView.MONTH,
  );

  const teamStats = useSelector(getAgentTeamStatsSelector);
  const agentRole = useSelector(getAgentTeamRoleSelector);

  const isWeekView = useMemo(() => view === CalendarView.WEEK, [view]);
  const isMonthView = useMemo(() => view === CalendarView.MONTH, [view]);
  const isShowTeamFilter = useMemo(() => agentRole !== TEAM_BASIC, [view]);
  const overdueMilestones = useMemo(() => query?.overdueMilestones, [query]);

  useEffect(() => {
    dispatch(requestGetTransactionPersons({ IsProject: false }, { silent: true }));
    dispatch(requestGetTransactionsEffect({ filters: {} }, { silent: true }));
    dispatch(requestGetProjectsEffect({ filters: { isProject: true } }, { silent: true }));
  }, []);

  useEffect(() => {
    setView(
      query?.dueToday || query?.overdueMilestones
        ? CalendarView.DAY
        : (LocalStorage.getCalendarViewType() as
            | CalendarView.MONTH
            | CalendarView.WEEK
            | CalendarView.DAY),
    );
  }, [LocalStorage.getCalendarViewType()]);

  useEffect(() => {
    return () => {
      // reset the filters run when navigating away
      dispatch(
        setCalendarFiltersEffect({
          type: defaultCalendarTypeOptions,
          agentsOrOwners: [],
          clients: [],
          transactionIds: [],
        }),
      );
    };
  }, [dispatch]);

  const fetchCalendarStats = (startDate, endDate) => {
    const assignee =
      teamStats?.itemValue === TeamFilterType.JustMe || agentRole === TEAM_BASIC
        ? 'Me'
        : teamStats?.itemValue === TeamFilterType.Team
        ? TeamFilterType.Team
        : teamStats?.userId;

    const overdueParams = {
      assignee: isShowTeamFilter ? assignee : TeamFilterType.Team,
      showOverdue: true,
      timePeriod: CalendarView.MONTH,
      type: ['Milestone'],
    };

    const calendarParams = {
      assignee: isShowTeamFilter ? assignee : TeamFilterType.Team,
      startDate: formatDate(startDate, 'YYYY-MM-DD'),
      endDate: formatDate(endDate, 'YYYY-MM-DD'),
      timePeriod: view,
      ...filters,
    };

    dispatch(getWorkshopCalendarStatsEffect(overdueMilestones ? overdueParams : calendarParams));
  };

  useEffect(() => {
    let newStartDate, newEndDate;

    if (isMonthView) {
      newStartDate = currentDate.clone().startOf(CalendarView.MONTH).startOf(CalendarView.WEEK);
      newEndDate = currentDate.clone().endOf(CalendarView.MONTH).endOf(CalendarView.WEEK);
    } else if (isWeekView) {
      newStartDate = currentDate.clone().startOf(CalendarView.WEEK);
      newEndDate = currentDate.clone().endOf(CalendarView.WEEK);
    } else {
      newStartDate = currentDate;
      newEndDate = currentDate;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);

    fetchCalendarStats(newStartDate, newEndDate);
  }, [currentDate, view, teamStats, filters]);

  const handleNext = () => setCurrentDate((prev) => prev.clone().add(1, view));

  const handlePrev = () => setCurrentDate((prev) => prev.clone().subtract(1, view));

  const renderDays = () => {
    if (!startDate || !endDate) return null;

    const days: any[] = [];
    let day = startDate.clone();

    while (!day.isAfter(endDate, 'day')) {
      const currentDay = day.clone();

      days.push(
        <div
          key={currentDay.format('YYYY-MM-DD')}
          className={classNames(styles.dayTile, {
            [styles.otherMonth]: isMonthView && currentDay.month() !== currentDate.month(),
            [styles.fullHeightTile]: isWeekView,
          })}
          onClick={() => {
            setCurrentDate(currentDay);
            setView(CalendarView.DAY);
            LocalStorage.setCalendarViewType(CalendarView.DAY);
          }}
        >
          <span
            className={classNames(styles.dayText, {
              [styles.today]: isMonthView && isToday(currentDay),
            })}
          >
            {isMonthView ? currentDay.date() : null}
          </span>
          <CalendarTiles
            calendarData={data}
            date={formatDate(currentDay, 'YYYY-MM-DD')}
            view={view}
          />
        </div>,
      );

      day.add(1, 'day'); // Move to the next day
    }

    return days;
  };

  const renderWeekdaysHeader = () => {
    const startOfWeek = currentDate.clone().startOf('week');
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
      const date = startOfWeek.clone().add(index, 'day');
      return (
        <div key={day} className={styles.weekday}>
          {isWeekView ? (
            <>
              {day}{' '}
              <span className={classNames(styles.weekdayText, { [styles.today]: isToday(date) })}>
                {date.format('D')}
              </span>
            </>
          ) : (
            <span className={styles.weekdayText}>{day}</span>
          )}
        </div>
      );
    });
  };

  return (
    <PageWrapper version={2} mainPageContentStyle={styles.mainPageContent} bgGray>
      <Header
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
        view={view}
        handleNext={handleNext}
        handlePrev={handlePrev}
        handleToday={() => setCurrentDate(moment())}
        setView={(view) => {
          setView(view);
          LocalStorage.setCalendarViewType(view);
        }}
        isShowTeamFilter={isShowTeamFilter}
        isOverdueMilestones={overdueMilestones}
      />
      <Wrapper isPending={isPending}>
        <div className={classNames(styles.calendarContainer, 'container')}>
          <div className={classNames(styles.content, { [styles.weekView]: isWeekView })}>
            {isMonthView || isWeekView ? (
              <>
                {isWeekView && (
                  <NavButtonLeft handlePrev={handlePrev} className={styles.weekViewNavBtnLeft} />
                )}
                <div className={styles.weekdays}>{renderWeekdaysHeader()}</div>
                {isWeekView && (
                  <NavButtonRight handleNext={handleNext} className={styles.weekViewNavBtnRight} />
                )}
                <div className={styles.calendarGrid}>{renderDays()}</div>
              </>
            ) : (
              <DayView
                date={formatDate(currentDate, 'YYYY-MM-DD')}
                calendarData={data}
                isOverdueMilestones={overdueMilestones}
              />
            )}
          </div>
        </div>
      </Wrapper>
      <Task />
    </PageWrapper>
  );
};
